import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import KneeImg from "../../../../../images/bannericons/knee.svg";
import HipImg from "../../../../../images/bannericons/hip.svg";
import HeadImg from "../../../../../images/bannericons/head.svg";
import ArmImg from "../../../../../images/bannericons/arm.svg";
import FootImg from "../../../../../images/bannericons/foot.svg";
import BriefcaseIcon from "../../../../assets/images/briefcase.svg";
import BannerImg from "../../../../assets/images/provider-profile/bannerimg.png";
import ProviderImg from "../../../../assets/images/defaultUserMOP.svg";
import { brand } from "../../../../../config/brands";

const ProviderBanner = ({
  name,
  jobTitle,
  reviewCount,
  yearsOfExperience,
  profileImage,
  description,
  businessName,
  placeId,
  apikey,
  isPractice,
  locationGroupId,
}) => {
  const navigate = useNavigate();
  const [overallRating, setOverallRating] = useState(null);
  const [totalReviews, setTotalReviews] = useState(0);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        let url;
        if (placeId) {
          url = `${
            process.env.REACT_APP_GOOGLE_REVIEW_URL
          }/reviews?placeId=${encodeURIComponent(placeId)}`;
        } else {
          url = `${
            process.env.REACT_APP_GOOGLE_REVIEW_URL
          }/reviews?places=${encodeURIComponent(businessName)}`;
        }
        const response = await fetch(url);

        const htmlText = await response.text();
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(htmlText, "text/html");

        // Initialize with default values
        let overallRating = null;
        let totalReviews = 0;

        // IMPROVED: Find total review count - more robust search
        const possibleReviewCountElements = [
          ...htmlDoc.querySelectorAll(
            '[aria-label*="review"], [aria-label*="Review"]'
          ),
          ...htmlDoc.querySelectorAll("p, span, div"),
        ];

        const reviewCountElement = possibleReviewCountElements.find((el) => {
          const text = el.textContent.trim();
          return (
            text.includes("Reviews") ||
            text.includes("reviews") ||
            text.includes("Review:") ||
            text.includes("review:")
          );
        });

        if (reviewCountElement) {
          const reviewsText = reviewCountElement.textContent.trim();
          const reviewsMatch = reviewsText.match(
            /([\d,]+)\s*(?:total\s*)?reviews?/i
          );
          if (reviewsMatch) {
            totalReviews = parseInt(reviewsMatch[1].replace(/,/g, ""));
          }
        }

        // Find rating - look for text containing "RATING" or rating meta
        const ratingElements = Array.from(
          htmlDoc.querySelectorAll('p, meta[itemprop="ratingValue"]')
        );
        const ratingElement = ratingElements.find(
          (el) =>
            el.textContent?.trim().includes("RATING") ||
            el.getAttribute?.("itemprop") === "ratingValue"
        );

        if (ratingElement) {
          const ratingMatch = ratingElement.textContent?.match(
            /(\d+\.\d+)/
          ) || [ratingElement.getAttribute("content")];
          if (ratingMatch && ratingMatch[0]) {
            overallRating = parseFloat(ratingMatch[0]);
          }
        }

        // Extract individual reviews
        const reviewCards = htmlDoc.querySelectorAll(
          '[class*="w-[300px]"], [class*="w-[386px]"], [class*="w-[500px]"]'
        );

        const extractedReviews = Array.from(reviewCards).map((card, index) => {
          // Get profile image - use direct src attribute
          const imgElement = card.querySelector("img.rounded-full");
          const avatar =
            imgElement?.src ||
            "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face";

          // Fix Google profile image URL by removing size parameters if they exist
          let finalAvatar = avatar;
          if (
            avatar.includes("googleusercontent.com") &&
            avatar.includes("=s")
          ) {
            finalAvatar = avatar.split("=s")[0] + "=s128"; // Standardize to 128px
          }

          const name =
            card.querySelector("h5")?.textContent.trim() || `User ${index + 1}`;
          const stars =
            card.querySelectorAll('[class*="text-yellow-300"]').length || 5;
          const date =
            card.querySelector('[class*="bg-green-100"]')?.textContent.trim() ||
            "Recent";
          const reviewText =
            card.querySelector("p:not(.text-gray-500)")?.textContent.trim() ||
            "No review text available";

          return {
            id: index + 1,
            name,
            avatar: finalAvatar,
            rating: stars,
            date,
            review:
              reviewText.length > 100
                ? `${reviewText.substring(0, 100)}...`
                : reviewText,
            fullReview: reviewText,
          };
        });

        setOverallRating(overallRating);
        setTotalReviews(totalReviews || reviewCount || extractedReviews.length);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };

    const shouldFetch = businessName || placeId;
    if (shouldFetch) {
      fetchReviews();
    }
  }, [businessName, placeId, reviewCount]);

  const handleBookAppointmentClick = () => {
    localStorage.setItem("provider_api_key", apikey);
    localStorage.setItem("provider_enhanced", "true");
    navigate(`/orthocheck?key=${apikey}`);
  };

  return (
    <section className="container pt-5">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li
            className="breadcrumb-item text-muted text-decoration-none hover-pointer"
            onClick={() => navigate("/providers")}
          >
            Providers
          </li>
          <li
            className="breadcrumb-item active text-primary fw-bold "
            aria-current="page"
            style={{ fontSize: "1.125rem" }}
          >
            {name}
          </li>
        </ol>
      </nav>

      <div className="row align-items-center">
        {/* Left Section */}
        <div className="col-md-6">
          <h2 className="fw-bold mb-1 ban-font">{name}</h2>
          <p
            className="text-uppercase text-muted d-flex align-items-center mb-3"
            style={{
              fontSize: "14px",
              fontWeight: "500",
              letterSpacing: "0.5px",
              color: "black",
            }}
          >
            <span className="fw-bold text-secondary">{jobTitle}</span>
            {yearsOfExperience && (
              <>
                <span
                  style={{
                    margin: "0 15px",
                    width: "1px",
                    height: "16px",
                    backgroundColor: "#dee2e6",
                    display: "inline-block",
                  }}
                ></span>
                <img
                  src={BriefcaseIcon}
                  alt="briefcase"
                  style={{
                    width: "16px",
                    height: "16px",
                    marginRight: "8px",
                  }}
                />
                <span className="text-secondary">
                  {yearsOfExperience} Years Experience
                </span>
              </>
            )}
          </p>
          <div
            className="text-secondary"
            style={{ maxWidth: "500px", fontSize: "16px" }}
          >
            {Array.isArray(description) ? (
              description.map((paragraph, index) => (
                <p key={index} className="mb-2">
                  {paragraph}
                </p>
              ))
            ) : (
              <p>{description}</p>
            )}
          </div>

          {/* CTA Buttons */}
          <div className="d-flex gap-3 mt-4 flex-wrap">
            <button
              className="button fw-semibold rounded"
              onClick={handleBookAppointmentClick}
              style={{
                fontSize: "16px",
                padding: "16px 30px",
                borderRadius: "8px",
              }}
            >
              {`Try ${brand.checkerName} Free`}
            </button>

            {/* <button
                className="btn btn-outline-secondary  fw-semibold d-flex align-items-center gap-2 text-center"
                style={{
                  fontSize: "16px",
                  backgroundColor: "#F2F6F2",
                  borderColor: "#82BF4C",
                  color: "#82BF4C",
                  justifyContent: "center",
                  padding: "16px 30px",
                }}
              >
                <i className="far fa-clock"></i>
                Request Appointment
              </button> */}
          </div>

          {/* Rating Section */}
          {overallRating && totalReviews > 0 ? (
            <div
              className="d-flex align-items-center mt-4 p-3"
              style={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                width: "fit-content",
              }}
            >
              {/* Stars */}
              <div
                className="d-flex align-items-center me-3"
                style={{ fontSize: "18px", color: "#FFD700" }}
              >
                <i className="fab fa-google fa px-1"></i>&nbsp;
                {[...Array(5)].map((_, i) => (
                  <i
                    key={i}
                    className={`fas ${
                      i < Math.floor(overallRating)
                        ? "fa-star"
                        : overallRating % 1 >= 0.5 &&
                          i === Math.floor(overallRating)
                        ? "fa-star-half-alt"
                        : "fa-star"
                    } me-1`}
                  ></i>
                ))}
              </div>

              {/* Rating */}
              <div
                className="fw-semibold text-dark"
                style={{ fontSize: "15px", paddingRight: "12px" }}
              >
                {overallRating.toFixed(1)} RATING
              </div>

              <div
                style={{
                  width: "1px",
                  height: "24px",
                  backgroundColor: "#e1e1e1",
                  marginRight: "12px",
                }}
              ></div>

              <div
                className="text-info-emphasis fw-semibold"
                style={{ fontSize: "15px" }}
              >
                {totalReviews} Reviews
              </div>
            </div>
          ) : null}
        </div>
        {isPractice ? (
          <div className="col-md-6 d-none d-md-block">
            <div
              className="position-relative d-flex justify-content-center align-items-center"
              style={{
                width: "100%",
              }}
            >
              <img
                src={profileImage || ProviderImg}
                alt={name}
                className="w-100"
              />
            </div>
          </div>
        ) : (
          <div className="col-md-6 d-none d-md-block position-relative d-flex justify-content-center align-items-center">
            <div
              className="position-relative"
              style={{
                width: "100%",
                maxWidth: "500px",
                aspectRatio: "1/1",
              }}
            >
              {/* Dashed Border Circle */}
              <svg
                className="absolute inset-0 w-full h-full block xl:hidden"
                viewBox="0 0 200 200"
              >
                <defs>
                  <linearGradient id="fade-mask" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="white"></stop>
                    <stop offset="0%" stop-color="white"></stop>
                    <stop offset="100%" stop-color="black"></stop>
                  </linearGradient>
                  <mask id="mask">
                    <rect
                      width="100%"
                      height="100%"
                      fill="url(#fade-mask)"
                    ></rect>
                  </mask>
                </defs>

                <circle
                  cx="100"
                  cy="100"
                  r="98"
                  fill="none"
                  stroke="#3B82F6"
                  stroke-width="1"
                  stroke-dasharray="15 15"
                  stroke-linecap="round"
                  mask="url(#mask)"
                ></circle>
              </svg>

              {/* Profile Image Circle */}
              <div
                className="rounded-circle overflow-hidden"
                style={{
                  width: "75%",
                  height: "75%",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <img
                  src={profileImage || BannerImg}
                  alt={name}
                  className="w-100 h-100"
                  style={{
                    objectFit: "cover",
                    objectPosition: "top",
                  }}
                />
              </div>

              {/* Icon - Neck (left-bottom) */}
              <div
                className="position-absolute"
                style={{
                  top: "60%",
                  left: "5%",
                  transform: "translate(-50%, -50%)",
                  padding: "10px",
                }}
              >
                <img
                  src={HeadImg}
                  alt="Neck"
                  style={{
                    width: "70px",
                    height: "65px",
                    objectFit: "contain",
                  }}
                />
              </div>

              {/* Icon - Hip (right-top) */}
              <div
                className="position-absolute "
                style={{
                  top: "10%",
                  right: "20%",
                  transform: "translate(50%, -50%)",
                  padding: "10px",
                }}
              >
                <img
                  src={HipImg}
                  alt="Hip"
                  style={{
                    width: "65px",
                    height: "65px",
                    objectFit: "contain",
                  }}
                />
              </div>

              {/* Icon - Elbow (right-bottom) */}
              <div
                className="position-absolute "
                style={{
                  bottom: "30%",
                  right: "5%",
                  transform: "translate(50%, 50%)",
                  padding: "10px",
                }}
              >
                <img
                  src={ArmImg}
                  alt="Elbow"
                  style={{
                    width: "65px",
                    height: "65px",
                    objectFit: "contain",
                  }}
                />
              </div>

              {/* Icon - Foot (right-inside) */}
              <div
                className="position-absolute "
                style={{
                  top: "40%",
                  right: "24%",
                  transform: "translate(100%, -60%)",
                  padding: "10px",
                }}
              >
                <img
                  src={FootImg}
                  alt="Foot"
                  style={{
                    width: "65px",
                    height: "65px",
                    objectFit: "contain",
                  }}
                />
              </div>

              {/* Icon - Knee (top-left-inside) */}
              <div
                className="position-absolute "
                style={{
                  bottom: "75%",
                  left: "28%",
                  transform: "translate(-100%, 50%)",
                  padding: "10px",
                }}
              >
                <img
                  src={KneeImg}
                  alt="Knee"
                  style={{
                    width: "65px",
                    height: "65px",
                    objectFit: "contain",
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProviderBanner;
